import { LiveScanNavIcon } from "@components/NavBar/LiveScanNavIcon";
import { Layout as SettingsLayout } from "@components/Settings/Layout";
import { TESLA_TAB_FEATURES } from "@components/Tesla/constants";
import BatteryIcon from "@lyteflo/assets/icons/battery-landscape-full.svg?react";
import BuildingIcon from "@lyteflo/assets/icons/building.svg?react";
import CarIcon from "@lyteflo/assets/icons/car.svg?react";
import DocumentIcon from "@lyteflo/assets/icons/document.svg?react";
import LogoutIcon from "@lyteflo/assets/icons/exit-door.svg?react";
import HomeIcon from "@lyteflo/assets/icons/home-outline.svg?react";
import MegaphoneIcon from "@lyteflo/assets/icons/megaphone.svg?react";
import ObdIcon from "@lyteflo/assets/icons/obd-port.svg?react";
import PersonOutlineIcon from "@lyteflo/assets/icons/person-outline.svg?react";
import PersonsIcon from "@lyteflo/assets/icons/persons.svg?react";
import TeslaIcon from "@lyteflo/assets/icons/tesla.svg?react";
import WifiIcon from "@lyteflo/assets/icons/wifi.svg?react";
import LytefloLogo from "@lyteflo/assets/logos/lyteflo-accent.svg?react";
import { FEATURE, PERMISSION_LEVEL, PortalRoutes } from "@lyteflo/types";
import { NavigationRouteConfig, RouteConfig } from "@portalTypes/routes";
import { isProd } from "@utils/environment";
import { lazy } from "preact/compat";
import { Redirect } from "wouter";

const AuthCallback = lazy(() =>
  import("@pages/AuthCallback").then((m) => ({ default: m.AuthCallback })),
);
const BatteryReport = lazy(() =>
  import("@pages/BatteryReport").then((m) => ({ default: m.BatteryReport })),
);
const BatteryReportPublic = lazy(() =>
  import("@pages/BatteryReportPublic").then((m) => ({
    default: m.BatteryReportPublic,
  })),
);
const CampaignSheetPublic = lazy(() =>
  import("@pages/CampaignSheetPublic").then((m) => ({
    default: m.CampaignSheetPublic,
  })),
);
const BatteryReports = lazy(() =>
  import("@pages/BatteryReports").then((m) => ({ default: m.BatteryReports })),
);
const Campaigns = lazy(() =>
  import("@pages/Campaigns").then((m) => ({ default: m.Campaigns })),
);
const CreateCampaign = lazy(() =>
  import("@pages/CreateCampaign").then((m) => ({
    default: m.CreateCampaign,
  })),
);
const CreateDealerConfig = lazy(() =>
  import("@pages/CreateDealerConfig").then((m) => ({
    default: m.CreateDealerConfig,
  })),
);
const EditDealerConfig = lazy(() =>
  import("@pages/EditDealerConfig").then((m) => ({
    default: m.EditDealerConfig,
  })),
);
const DealerConfig = lazy(() =>
  import("@pages/DealerConfig").then((m) => ({ default: m.DealerConfig })),
);
const Devices = lazy(() =>
  import("@pages/Devices").then((m) => ({ default: m.Devices })),
);
const EditCampaign = lazy(() =>
  import("@pages/EditCampaign").then((m) => ({ default: m.EditCampaign })),
);
const FormShellDemo = lazy(() =>
  import("@pages/FormShellDemo").then((m) => ({ default: m.FormShellDemo })),
);
const Home = lazy(() =>
  import("@pages/Home").then((m) => ({ default: m.Home })),
);
const LiveScan = lazy(() =>
  import("@pages/LiveScan").then((m) => ({ default: m.LiveScan })),
);
const Login = lazy(() =>
  import("@pages/Login").then((m) => ({ default: m.Login })),
);
const Logout = lazy(() =>
  import("@pages/Logout").then((m) => ({ default: m.Logout })),
);
const Lyteflo = lazy(() =>
  import("@pages/Lyteflo").then((m) => ({ default: m.Lyteflo })),
);
const OwnershipReport = lazy(() =>
  import("@pages/OwnershipReport").then((m) => ({
    default: m.OwnershipReport,
  })),
);
const Profile = lazy(() =>
  import("@pages/Profile").then((m) => ({ default: m.Profile })),
);
const Tesla = lazy(() =>
  import("@pages/Tesla").then((m) => ({ default: m.Tesla })),
);
const TeslaAccount = lazy(() =>
  import("@pages/TeslaAccount").then((m) => ({ default: m.TeslaAccount })),
);
const Test = lazy(() =>
  import("@pages/Test").then((m) => ({ default: m.Test })),
);
const UnscannedInventory = lazy(() =>
  import("@pages/UnscannedInventory").then((m) => ({
    default: m.UnscannedInventory,
  })),
);
const Unsubscribe = lazy(() =>
  import("@pages/Unsubscribe").then((m) => ({ default: m.Unsubscribe })),
);
const Users = lazy(() =>
  import("@pages/Users").then((m) => ({ default: m.Users })),
);
const VehicleProfileBrowserPage = lazy(() =>
  import("@pages/VehicleProfileBrowserPage").then((m) => ({
    default: m.VehicleProfileBrowserPage,
  })),
);
const VehicleSupport = lazy(() =>
  import("@pages/VehicleSupport").then((m) => ({ default: m.VehicleSupport })),
);
const WifiConfig = lazy(() =>
  import("@pages/WifiConfiguration").then((m) => ({ default: m.WifiConfig })),
);

export const navigationRoutes: NavigationRouteConfig[] = [
  {
    path: PortalRoutes.HOME,
    labelKey: "navigation.home",
    component: Home,
    isProtected: true,
    feature: FEATURE.VIEW_HOME,
    icon: HomeIcon,
  },
  {
    path: PortalRoutes.LIVE_SCANS,
    labelKey: "navigation.liveScan",
    component: LiveScan,
    isProtected: true,
    feature: FEATURE.VIEW_LIVE_SCAN,
    icon: LiveScanNavIcon as React.FunctionComponent,
  },
  {
    path: PortalRoutes.BATTERY_REPORTS,
    labelKey: "navigation.reports",
    component: BatteryReports,
    isProtected: true,
    feature: FEATURE.VIEW_REPORTS,
    icon: BatteryIcon,
  },
  {
    path: PortalRoutes.UNSCANNED_INVENTORY,
    labelKey: "navigation.unscanned",
    component: UnscannedInventory,
    isProtected: true,
    feature: FEATURE.VIEW_UNSCANNED_INVENTORY,
    icon: CarIcon,
  },
  {
    path: PortalRoutes.TESLA,
    labelKey: "navigation.tesla",
    component: Tesla,
    isProtected: true,
    feature: TESLA_TAB_FEATURES,
    icon: TeslaIcon,
  },
];

export const settingsRoutes: NavigationRouteConfig[] = [
  {
    path: PortalRoutes.PROFILE,
    labelKey: "navigation.profile",
    component: Profile,
    isProtected: true,
    feature: FEATURE.VIEW_PROFILE,
    icon: PersonOutlineIcon,
    layout: SettingsLayout,
  },
  {
    path: PortalRoutes.USERS,
    labelKey: "navigation.users",
    component: Users,
    isProtected: true,
    feature: FEATURE.VIEW_USERS,
    icon: PersonsIcon,
    layout: SettingsLayout,
  },
  {
    path: PortalRoutes.DEVICES,
    labelKey: "navigation.devices",
    component: Devices,
    isProtected: true,
    feature: FEATURE.VIEW_DEVICES,
    icon: ObdIcon,
    layout: SettingsLayout,
  },
  {
    path: PortalRoutes.WIFI_CONFIG,
    labelKey: "navigation.wifiConfig",
    component: WifiConfig,
    isProtected: true,
    feature: FEATURE.VIEW_WIFI_CONFIG,
    icon: WifiIcon,
    layout: SettingsLayout,
  },
  {
    path: PortalRoutes.TESLA_ACCOUNT,
    labelKey: "navigation.teslaAccount",
    component: TeslaAccount,
    isProtected: true,
    feature: FEATURE.MANAGE_TESLA_ACCOUNT,
    icon: TeslaIcon,
    layout: SettingsLayout,
  },
  {
    path: PortalRoutes.LYTEFLO,
    labelKey: "navigation.lyteflo",
    component: Lyteflo,
    isProtected: true,
    requiredLevel: PERMISSION_LEVEL.LF_USER,
    icon: LytefloLogo,
    iconClassName: "lf-rounded-sm",
    layout: SettingsLayout,
  },
  {
    path: PortalRoutes.LOGOUT,
    labelKey: "navigation.logout",
    component: Logout,
    isProtected: false,
    icon: LogoutIcon,
    iconColorClassName: "lf-text-[var(--red-11)]",
  },
];

export const lytefloRoutes: NavigationRouteConfig[] = [
  {
    path: PortalRoutes.VEHICLE_SUPPORT,
    labelKey: "navigation.vehicleSupport",
    component: VehicleSupport,
    isProtected: true,
    feature: FEATURE.VIEW_VEHICLE_SUPPORT,
    icon: CarIcon,
    layout: SettingsLayout,
  },
  {
    path: PortalRoutes.CAMPAIGNS,
    labelKey: "navigation.campaigns",
    component: Campaigns,
    isProtected: true,
    feature: FEATURE.VIEW_CAMPAIGNS,
    icon: MegaphoneIcon,
    layout: SettingsLayout,
  },
  {
    path: PortalRoutes.DEALER_CONFIG,
    labelKey: "navigation.dealerConfig",
    component: DealerConfig,
    isProtected: true,
    feature: FEATURE.VIEW_DEALER_CONFIG,
    icon: BuildingIcon,
    layout: SettingsLayout,
  },
  {
    path: PortalRoutes.VEHICLE_PROFILE_BROWSER,
    labelKey: "navigation.vehicleProfileBrowser",
    component: VehicleProfileBrowserPage,
    isProtected: true,
    feature: FEATURE.VIEW_VEHICLE_PROFILE_BROWSER,
    icon: DocumentIcon,
    layout: SettingsLayout,
    hidden: isProd,
  },
];

export const routes: RouteConfig[] = [
  {
    path: PortalRoutes.ROOT,
    component: () => <Redirect to={PortalRoutes.HOME} />,
  },
  { path: PortalRoutes.LOGIN, component: Login },
  { path: PortalRoutes.LYTEFLOGIN, component: Login },
  { path: PortalRoutes.AUTH_CALLBACK, component: AuthCallback },
  {
    path: PortalRoutes.BATTERY_REPORT,
    component: BatteryReport,
    isProtected: true,
    fullPage: true,
    feature: FEATURE.VIEW_REPORT,
  },
  {
    path: PortalRoutes.BATTERY_REPORT_PUBLIC,
    component: BatteryReportPublic,
    isProtected: false,
    fullPage: true,
  },
  {
    path: PortalRoutes.CAMPAIGN_SHEET_PUBLIC,
    component: CampaignSheetPublic,
    isProtected: false,
    fullPage: true,
  },
  {
    path: PortalRoutes.WIDGET_OWNERSHIP_REPORT,
    component: OwnershipReport,
    isProtected: false,
  },
  {
    path: PortalRoutes.WIDGET_UNSUBSCRIBE,
    component: Unsubscribe,
    isProtected: false,
  },
  { path: PortalRoutes.TEST, component: Test, isProtected: false },
  {
    path: `${PortalRoutes.TEST}/form-shell`,
    component: FormShellDemo,
    isProtected: false,
  },
  ...settingsRoutes,
  ...lytefloRoutes.map((tab) => ({
    path: tab.path,
    component: Lyteflo,
    isProtected: true,
    layout: SettingsLayout,
    feature: tab.feature,
  })),
  ...navigationRoutes,
  {
    path: PortalRoutes.CAMPAIGN_CREATE,
    component: CreateCampaign,
    isProtected: true,
    feature: FEATURE.CREATE_CAMPAIGN,
  },
  {
    path: PortalRoutes.CAMPAIGN_EDIT,
    component: EditCampaign,
    isProtected: true,
    feature: FEATURE.EDIT_CAMPAIGN,
  },
  {
    path: PortalRoutes.DEALER_CONFIG_CREATE,
    component: CreateDealerConfig,
    isProtected: true,
    feature: FEATURE.CREATE_DEALER_CONFIG,
  },
  {
    path: PortalRoutes.DEALER_CONFIG_EDIT,
    component: EditDealerConfig,
    isProtected: true,
    feature: FEATURE.EDIT_DEALER_CONFIG,
  },
];

export const mainNavigation = navigationRoutes.map(
  ({
    path,
    labelKey,
    icon: Icon,
    feature,
    iconClassName,
    iconColorClassName,
    hidden,
  }) => {
    const href = path;

    return {
      labelKey,
      href,
      ...(Icon && { Icon }),
      iconClassName,
      iconColorClassName,
      feature,
      hidden,
    };
  },
);
